import { UniDriver } from '@wix/wix-ui-test-utils/unidriver';
import {
  MessageModalLayoutUniDriver,
  DropdownUniDriver,
  FormFieldUniDriver,
  CheckboxUniDriver,
  InputUniDriver,
  TextButtonUniDriver,
  LoaderUniDriver,
  FloatingNotificationUniDriver,
  TextUniDriver,
} from '@wix/design-system/dist/testkit/unidriver';

export function CustomFieldModalUniDriver(_base: UniDriver, body: UniDriver) {
  const getCustomFieldModal = () =>
    MessageModalLayoutUniDriver(
      body.$('[data-hook="custom-field-modal"]'),
      body,
    );
  const getSettingsButton = () =>
    TextButtonUniDriver(
      body.$('[data-hook="custom-field-modal-settings-button"]'),
      body,
    );
  const getErrorStateNotification = () =>
    FloatingNotificationUniDriver(
      body.$('[data-hook="custom-field-modal-error-state"]'),
      body,
    );
  const getLoader = () =>
    LoaderUniDriver(
      body.$('[data-hook="custom-field-modal-status-submitting"]'),
      body,
    );

  const getFieldKeyTooltip = () =>
    TextUniDriver(
      body.$('[data-hook="custom-field-modal-fieldKey-tooltip"]'),
      body,
    );

  const exists = () => getCustomFieldModal().exists();
  const clickConfirm = () =>
    getCustomFieldModal()
      .getPrimaryButtonDriver()
      .then((driver: any) => driver.click());
  const clickClose = () => getCustomFieldModal().clickCloseButton();
  const clickCancel = () =>
    getCustomFieldModal()
      .getSecondaryButtonDriver()
      .then((driver: any) => driver.click());
  const clickAdvancedSettings = () => getSettingsButton().click();
  const isSubmitting = () => getLoader().exists();

  const getFormTypeDriver = () =>
    DropdownUniDriver(body.$('[data-hook="custom-field-modal-type"]'), body);
  const getFormNameDriver = () =>
    InputUniDriver(body.$('[data-hook="custom-field-modal-name"]'), body);
  const getFormKeyDriver = () =>
    InputUniDriver(body.$('[data-hook="custom-field-modal-key"]'), body);
  const getFormReadApps = () =>
    CheckboxUniDriver(
      body.$('[data-hook="custom-field-modal-read-apps"]'),
      body,
    );
  const getFormWriteApps = () =>
    CheckboxUniDriver(
      body.$('[data-hook="custom-field-modal-write-apps"]'),
      body,
    );
  const getFormReadUsers = () =>
    CheckboxUniDriver(
      body.$('[data-hook="custom-field-modal-read-users"]'),
      body,
    );
  const getFormWriteUsers = () =>
    CheckboxUniDriver(
      body.$('[data-hook="custom-field-modal-write-users"]'),
      body,
    );

  const getTypeFormFieldDriver = () =>
    FormFieldUniDriver(
      body.$('[data-hook="custom-field-modal-field-type"]'),
      body,
    );
  const getNameFormFieldDriver = () =>
    FormFieldUniDriver(
      body.$('[data-hook="custom-field-modal-field-name"]'),
      body,
    );
  const getKeyFormFieldDriver = () =>
    FormFieldUniDriver(
      body.$('[data-hook="custom-field-modal-field-key"]'),
      body,
    );

  const getFormDrivers = () => ({
    getFormTypeDriver,
    getFormNameDriver,
    getFormKeyDriver,
    getFormReadApps,
    getFormWriteApps,
    getFormReadUsers,
    getFormWriteUsers,
    getTypeFormFieldDriver,
    getNameFormFieldDriver,
    getKeyFormFieldDriver,
    getFieldKeyTooltip,
  });

  const fillInCustomFieldForm = async ({
    type,
    name,
    key,
    permissions,
  }: {
    type?: string;
    name?: string;
    key?: string;
    permissions?: {
      read?: ('apps' | 'users')[];
      write?: ('apps' | 'users')[];
    };
  }) => {
    if (type) {
      await getFormTypeDriver().dropdownLayoutDriver.clickAtOptionByDataHook(
        `custom-field-modal-type-${type}`,
      );
    }

    if (name) {
      await getFormNameDriver().enterText(name);
    }

    if (key) {
      await getFormKeyDriver().enterText(key);
    }

    if (permissions) {
      (
        [
          [getFormReadApps(), permissions?.read?.includes('apps') ?? false],
          [getFormWriteApps(), permissions?.write?.includes('apps') ?? false],
          [getFormReadUsers(), permissions?.read?.includes('users') ?? false],
          [getFormWriteUsers(), permissions?.write?.includes('users') ?? false],
        ] as [ReturnType<typeof CheckboxUniDriver>, boolean][]
      ).forEach(async ([driver, value]) => {
        const isChecked = await driver.isChecked();

        if ((value && !isChecked) || (!value && isChecked)) {
          await driver.click();
        }
      });
    }
  };

  const getCustomFieldFormValues = async () => {
    return {
      type: await getFormTypeDriver().inputDriver.getValue(),
      name: await getFormNameDriver().getValue(),
      key: await getFormKeyDriver().getValue(),
      permissions: {
        read: {
          apps: await getFormReadApps().isChecked(),
          users: await getFormReadUsers().isChecked(),
        },
        write: {
          apps: await getFormWriteApps().isChecked(),
          users: await getFormWriteUsers().isChecked(),
        },
      },
    };
  };

  return {
    exists,
    clickConfirm,
    clickClose,
    clickCancel,
    clickAdvancedSettings,
    fillInCustomFieldForm,
    getCustomFieldFormValues,
    getFormDrivers,
    getErrorStateNotification,
    isSubmitting,
  };
}
